import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Avatar } from '../components/Avatar'
import {
  buehneWaehlen,
  fetchBuehneModus,
  fetchBuehneZugang,
  fetchIntroState,
  openIntroChat,
  sendIntroCommand,
  setBuehneModus,
  type BuehnenModus,
  type IntroZustand,
} from '../lib/api'
import { GewinnspielRemotePage } from './GewinnspielRemotePage'
import { IntroRemotePage } from './IntroRemotePage'
import { PraesentationRemotePage } from './PraesentationRemotePage'

/** Wie oft das Handy nachsieht, ob jemand anderes umgeschaltet hat. */
const MODUS_POLL_MS = 2000
/** Im Gespräch: wie oft nachsehen, ob der Bildschirm den Chat schon zeigt. */
const INTRO_POLL_MS = 1000
/**
 * So viele Meldungen „kein Chat“ in Folge, bevor das Handy die Chat-Steuerung
 * wieder zuklappt. Eine einzelne reicht nicht: Lädt der Bildschirm neu oder
 * meldet er beim Umschalten kurz „wartet“, würde sonst die Eingabe samt
 * halb getipptem Text verschwinden und gleich wiederkommen.
 */
const KEIN_CHAT_TOLERANZ = 3

const MODI: { id: BuehnenModus; label: string }[] = [
  { id: 'intro', label: 'Intro' },
  { id: 'gespraech', label: 'Gespräch' },
  { id: 'gewinnspiel', label: 'Gewinnspiel' },
  { id: 'praesentation', label: 'Präsentation' },
]

/** Höhe der Leiste oben; die eingebetteten Seiten ziehen sie von ihrer Höhe ab. */
const LEISTE = '3.75rem'

/**
 * `/buehne/go/<token>`: die gemeinsame Fernbedienung für den Bildschirm
 * `/buehne` (Flo, 08.10.). Oben wählt man, was der große Bildschirm zeigt —
 * Intro, Gespräch oder Gewinnspiel —, darunter steht die gewohnte Steuerung
 * dafür: dieselbe wie auf `/intro/go/…` und `/gewinnspiel/go/…`.
 *
 * - **Intro:** Intro starten, Boxen, Chat — wie am Intro-Tag.
 * - **Gespräch:** öffnet gleich einen neuen, leeren Chat; getippt oder
 *   gesprochen wird hier, K4ia antwortet am Bildschirm.
 * - **Gewinnspiel:** Ziehung starten, abhaken, neu ziehen.
 * - **Präsentation:** Folie für Folie weiterklicken, an den Quartalsfolien
 *   entscheiden, zum Schluss Fragen aus dem Publikum (PraesentationRemotePage).
 *
 * Die Tokens von Intro und Gewinnspiel holt die Seite mit ihrem eigenen
 * Token (`/api/v1/buehne/zugang/<token>`).
 */
export function BuehneRemotePage({ token }: { token: string }) {
  useState(() => buehneWaehlen('intro'))
  const [zugang, setZugang] = useState<{ introToken: string; gewinnspielToken: string } | null>(
    null,
  )
  const [modus, setModus] = useState<BuehnenModus | null>(null)
  const [wechselt, setWechselt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  /** Solange ein Wechsel unterwegs ist, gilt die eigene Wahl, nicht der gelesene Modus. */
  const wechseltRef = useRef(false)

  useEffect(() => {
    void fetchBuehneZugang(token)
      .then(setZugang)
      .catch(() => setFehler('Diese Adresse gilt nicht. Bitte die Adresse aus dem Admin nehmen.'))
  }, [token])

  // Mitlesen, falls ein zweites Handy umschaltet.
  useEffect(() => {
    let aktiv = true
    const lesen = () =>
      void fetchBuehneModus()
        .then((m) => {
          if (aktiv) setModus((alt) => (wechseltRef.current ? alt : m.modus))
        })
        .catch(() => undefined)
    lesen()
    const id = setInterval(lesen, MODUS_POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [])

  async function wechseln(neu: BuehnenModus) {
    if (!zugang || neu === modus || wechselt) return
    const vorher = modus
    setFehler(null)
    setWechselt(true)
    wechseltRef.current = true
    setModus(neu)
    try {
      // Erst den Chat öffnen, dann umschalten: So steht die Sitzung schon,
      // wenn der Bildschirm in den Gesprächsmodus wechselt.
      if (neu === 'gespraech') await openIntroChat(zugang.introToken)
      // Vom Gespräch zurück ins Intro: der Bildschirm wartet wieder aufs Intro.
      if (neu === 'intro' && vorher === 'gespraech') {
        await sendIntroCommand(zugang.introToken, { art: 'ende' })
      }
      await setBuehneModus(token, neu)
    } catch (e) {
      setModus(vorher)
      setFehler(e instanceof Error ? e.message : 'Umschalten hat nicht geklappt.')
    } finally {
      wechseltRef.current = false
      setWechselt(false)
    }
  }

  const leiste = (
    <nav
      aria-label="Was der Bildschirm zeigt"
      className="sticky top-0 z-20 flex h-[3.75rem] items-center gap-1.5 border-b border-border bg-bg/95 px-3 backdrop-blur"
    >
      {MODI.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => void wechseln(m.id)}
          disabled={!zugang || wechselt}
          aria-pressed={modus === m.id}
          className={`flex-1 rounded-xl px-2 py-2.5 text-sm font-semibold transition ${
            modus === m.id
              ? 'bg-accent text-on-accent'
              : 'border border-border bg-bg-elevated text-text'
          } disabled:opacity-70`}
        >
          {m.label}
        </button>
      ))}
    </nav>
  )

  const stil = { '--buehne-leiste': LEISTE } as CSSProperties

  if (!zugang || !modus) {
    return (
      <div style={stil}>
        {leiste}
        <Rahmen avatar={fehler ? 'error' : 'thinking'}>
          <p className="text-xl">{fehler ?? 'Einen Moment …'}</p>
        </Rahmen>
      </div>
    )
  }

  return (
    <div style={stil} data-testid="buehne-handy" data-modus={modus}>
      {leiste}
      {fehler && (
        <p className="px-4 pt-3 text-center text-sm text-status-error" role="alert">
          {fehler}
        </p>
      )}
      {modus === 'intro' && <IntroRemotePage key="intro" token={zugang.introToken} />}
      {modus === 'gespraech' && <Gespraech introToken={zugang.introToken} />}
      {modus === 'gewinnspiel' && (
        <GewinnspielRemotePage key="gewinnspiel" token={zugang.gewinnspielToken} />
      )}
      {modus === 'praesentation' && (
        <PraesentationRemotePage key="praesentation" token={token} sttToken={zugang.introToken} />
      )}
    </div>
  )
}

/**
 * Modus „Gespräch“: Steht am Bildschirm der Chat, die gewohnte
 * Chat-Steuerung des Intros. Sonst (gerade umgeschaltet, oder der Chat wurde
 * dort beendet) ein Knopf, der ein neues Gespräch öffnet.
 */
function Gespraech({ introToken }: { introToken: string }) {
  /** Nur, was die Anzeige braucht — und nur neu setzen, wenn es sich ändert, sonst rendert alles darunter jede Sekunde neu. */
  const [zustand, setZustand] = useState<{ chat: boolean; verbunden: boolean } | null>(null)
  const [oeffnet, setOeffnet] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const keinChatSeit = useRef(0)

  useEffect(() => {
    let aktiv = true
    const lesen = () =>
      void fetchIntroState()
        .then((z: IntroZustand) => {
          if (!aktiv) return
          const imChat = z.bildschirm.phase === 'chat'
          keinChatSeit.current = imChat ? 0 : keinChatSeit.current + 1
          setZustand((alt) => {
            const chat = imChat || (alt?.chat === true && keinChatSeit.current < KEIN_CHAT_TOLERANZ)
            return alt && alt.chat === chat && alt.verbunden === z.verbunden
              ? alt
              : { chat, verbunden: z.verbunden }
          })
        })
        .catch(() => undefined)
    lesen()
    const id = setInterval(lesen, INTRO_POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [])

  if (zustand?.chat) {
    return <IntroRemotePage key="gespraech" token={introToken} />
  }

  async function oeffnen() {
    setFehler(null)
    setOeffnet(true)
    try {
      await openIntroChat(introToken)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das Gespräch ließ sich nicht öffnen.')
    } finally {
      setOeffnet(false)
    }
  }

  const verbunden = zustand?.verbunden ?? false
  return (
    <Rahmen avatar={fehler ? 'error' : 'idle'}>
      <p className="text-xl">Gespräch mit K4ia</p>
      <p className="text-text-muted">
        {!zustand
          ? 'Einen Moment …'
          : verbunden
            ? 'Der Bildschirm öffnet das Gespräch …'
            : 'Der Bildschirm ist nicht verbunden. Öffne dort /buehne und klicke einmal hinein.'}
      </p>
      <button
        onClick={() => void oeffnen()}
        disabled={oeffnet}
        className="btn-primary w-full rounded-2xl px-8 py-5 text-xl font-semibold"
      >
        {oeffnet ? 'Öffnet …' : 'Neues Gespräch öffnen'}
      </button>
      {fehler && (
        <p className="text-center text-sm text-status-error" role="alert">
          {fehler}
        </p>
      )}
    </Rahmen>
  )
}

function Rahmen({
  avatar,
  children,
}: {
  avatar: 'idle' | 'thinking' | 'speaking' | 'error'
  children: ReactNode
}) {
  return (
    <main className="flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] flex-col items-center justify-center gap-6 bg-bg p-8 text-center">
      <Avatar state={avatar} size={140} showLabel={false} />
      <div className="flex w-full max-w-sm flex-col items-center gap-4">{children}</div>
    </main>
  )
}
